import {
	resetDialogConfirm,
	setDialogOpen,
	setInjectionState,
} from 'feature/hooks/dialogSlice';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';

import {
	deleteComplaint,
	deleteFraud,
	sendToReview,
} from 'feature/claims/claimSlice';
import {
	CHECK_CONNECTION_ERROR,
	DELETE_COMPLAINT,
	DELETE_FRAUD,
	GENERIC_ERROR,
	NO_PROVIDER,
	SEND_TO_REVIEW,
	TOKEN_EXPIRED,
	VERSION,
} from 'types/dialogTypes';
import { useLogout } from './login.hook';

const useDialogConfirm = () => {
	const dispatch = useDispatch<AppDispatch>();

	const { type } = useSelector((store: RootState) => store.hooks.dialog);

	const mapping = useCallback((type: string, text: string) => {
		switch (type) {
			case GENERIC_ERROR:
				return {
					title: 'dialog.confirm.error.title',
					content: text || 'dialog.confirm.error.content',
					labelCancel: 'dialog.buttons.cancel',
					variantButtonCancel: 'contained',
					type: type,
					buttonClose: true,
				};
			case VERSION:
				return {
					title: 'dialog.version.title',
					content: 'dialog.version.contentTitle',
					type: 'version',
					buttonClose: true,
				};
			case SEND_TO_REVIEW:
				return {
					title: 'dialog.sendToReview.title',
					content: text,
					type: type,
					buttonClose: true,
					labelCancel: 'dialog.buttons.cancel',
					labelConfirm: 'dialog.buttons.confirm',
					variantButtonCancel: 'outlined',
				};
			case DELETE_COMPLAINT:
				return {
					title: 'dialog.deleteComplaint',
					content: text,
					type: type,
					buttonClose: false,
					labelCancel: 'dialog.buttons.cancel',
					labelConfirm: 'dialog.buttons.confirm',
					variantButtonCancel: 'outlined',
				};
			case DELETE_FRAUD:
				return {
					title: 'dialog.deleteFraud',
					content: text,
					type: type,
					buttonClose: false,
					labelCancel: 'dialog.buttons.cancel',
					labelConfirm: 'dialog.buttons.confirm',
					variantButtonCancel: 'outlined',
				};
			default:
		}
	}, []);

	const handleCancel = useCallback(() => {
		dispatch(setDialogOpen(false));
	}, [dispatch]);

	const handleLogout = useLogout();

	const closeDialog = useCallback(() => {
		dispatch(setDialogOpen(false));
		dispatch(resetDialogConfirm());

		switch (type) {
			case TOKEN_EXPIRED:
				handleLogout();
				break;
			default:
				break;
		}
	}, [dispatch, handleLogout, type]);

	const handleOpen = useCallback(
		(type: string, text?: string) => {
			dispatch(setInjectionState(mapping(type, text || '')));
			dispatch(setDialogOpen(true));
		},
		[dispatch, mapping],
	);

	const handleConfirm = useCallback(() => {
		dispatch(resetDialogConfirm());
		switch (type) {
			case NO_PROVIDER:
			case CHECK_CONNECTION_ERROR:
			case TOKEN_EXPIRED:
				handleLogout();
				break;
			case SEND_TO_REVIEW:
				dispatch(sendToReview(''));
				break;
			case DELETE_COMPLAINT:
				dispatch(deleteComplaint(''));
				break;
			case DELETE_FRAUD:
				dispatch(deleteFraud(''));
				break;
			default:
				break;
		}
		dispatch(setDialogOpen(false));
	}, [dispatch, handleLogout, type]);

	return useMemo(
		() => ({
			open: handleOpen,
			close: closeDialog,
			confirm: handleConfirm,
			cancel: handleCancel,
		}),
		[closeDialog, handleCancel, handleConfirm, handleOpen],
	);
};

export default useDialogConfirm;
